import {
  createAsyncThunk,
  createSlice,
  type ActionReducerMapBuilder,
  type AsyncThunk,
  type PayloadAction,
} from "@reduxjs/toolkit";
import type { RootState } from "@/store/store";
import { isFetchResult, reusableWithoutTokenFetch } from "@/utils/useFetch";

const AUTH_V2_BASE_PATH = "auth/v2";

const AUTH_REQUEST_KEYS = {
  checkSession: "checkSession",
  passwordLogin: "passwordLogin",
  magicSend: "magicSend",
  magicVerify: "magicVerify",
  mfaVerify: "mfaVerify",
  mfaEnrol: "mfaEnrol",
  passwordResetRequest: "passwordResetRequest",
  passwordResetConfirm: "passwordResetConfirm",
  emailVerify: "emailVerify",
  invitationStatus: "invitationStatus",
  invitationName: "invitationName",
  invitationPassword: "invitationPassword",
  invitationMfaEnrol: "invitationMfaEnrol",
  invitationMfaVerify: "invitationMfaVerify",
  invitationAccept: "invitationAccept",
  organisationSelect: "organisationSelect",
} as const;

type AuthRequestKey = (typeof AUTH_REQUEST_KEYS)[keyof typeof AUTH_REQUEST_KEYS];
type AuthApiResponse = Record<string, unknown>;
type AuthRequestPayload = Record<string, unknown> | undefined;
type AuthThunkFulfilledMeta = { fulfilledAt: number };

interface AuthPendingMfa {
  pendingAuthenticationToken: string;
  authenticationChallengeId: string;
}

export interface AuthOrganisation {
  id: string;
  name: string;
}

interface AuthUser {
  id: string | null;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

interface AuthV2Error {
  code: string;
  message: string;
  retryAfterSeconds?: number;
  mfa?: AuthPendingMfa;
  workOsCode?: string;
  userId?: string;
  pendingAuthenticationToken?: string;
  organisations?: AuthOrganisation[];
  errorDescriptions?: string[];
}

interface AuthSessionState {
  checked: boolean;
  authenticated: boolean;
  reason: string | null;
  sessionId: string | null;
  organizationId: string | null;
  role: string | null;
  permissions: unknown[];
  user: AuthUser | null;
}

interface AuthMagicChallenge {
  challengeId: string | null;
  expiresAt: string | null;
  email: string | null;
}

export interface AuthMagicOTP {
  email: string | null;
  triggeredAt: number | null;
}

export interface AuthInvitationDetails {
  email: string | null;
  tenancyId: string | null;
  tenancyName: string | null;
  organisationName: string | null;
  state: string | null;
  expiresAt: string | null;
}

export interface AuthInvitationSetup {
  nameConfigured: boolean;
  passwordConfigured: boolean;
  mfaConfigured: boolean;
}

export interface AuthInvitationAccount {
  existing: boolean;
  requiresSignInToAccept: boolean;
}

interface AuthInvitationStatus {
  invitation: AuthInvitationDetails | null;
  setup: AuthInvitationSetup | null;
  account: AuthInvitationAccount | null;
}

interface AuthV2State {
  session: AuthSessionState;
  magicChallenge: AuthMagicChallenge | null;
  magicOTP: AuthMagicOTP | null;
  pendingMfa: AuthPendingMfa | null;
  invitationStatus: AuthInvitationStatus | null;
  loading: Record<AuthRequestKey, boolean>;
  errors: Record<AuthRequestKey, AuthV2Error | null>;
}

export interface MfaEnrolmentData {
  challengeId?: string;
  totp?: {
    qrCode: string;
    secret: string;
    uri: string;
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === "object";
};

const toStringOrNull = (value: unknown) => {
  return typeof value === "string" ? value : null;
};

const toStringOrUndefined = (value: unknown) => {
  return typeof value === "string" ? value : undefined;
};

const toPendingMfa = (value: unknown): AuthPendingMfa | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const pendingAuthenticationToken = toStringOrUndefined(value.pendingAuthenticationToken);
  const authenticationChallengeId = toStringOrUndefined(value.authenticationChallengeId);
  if (!pendingAuthenticationToken || !authenticationChallengeId) {
    return undefined;
  }

  return {
    pendingAuthenticationToken,
    authenticationChallengeId,
  };
};

const initialLoadingState = {
  [AUTH_REQUEST_KEYS.checkSession]: false,
  [AUTH_REQUEST_KEYS.passwordLogin]: false,
  [AUTH_REQUEST_KEYS.magicSend]: false,
  [AUTH_REQUEST_KEYS.magicVerify]: false,
  [AUTH_REQUEST_KEYS.mfaVerify]: false,
  [AUTH_REQUEST_KEYS.mfaEnrol]: false,
  [AUTH_REQUEST_KEYS.passwordResetRequest]: false,
  [AUTH_REQUEST_KEYS.passwordResetConfirm]: false,
  [AUTH_REQUEST_KEYS.emailVerify]: false,
  [AUTH_REQUEST_KEYS.invitationStatus]: false,
  [AUTH_REQUEST_KEYS.invitationName]: false,
  [AUTH_REQUEST_KEYS.invitationPassword]: false,
  [AUTH_REQUEST_KEYS.invitationMfaEnrol]: false,
  [AUTH_REQUEST_KEYS.invitationMfaVerify]: false,
  [AUTH_REQUEST_KEYS.invitationAccept]: false,
  [AUTH_REQUEST_KEYS.organisationSelect]: false,
};

const initialErrorState = {
  [AUTH_REQUEST_KEYS.checkSession]: null,
  [AUTH_REQUEST_KEYS.passwordLogin]: null,
  [AUTH_REQUEST_KEYS.magicSend]: null,
  [AUTH_REQUEST_KEYS.magicVerify]: null,
  [AUTH_REQUEST_KEYS.mfaVerify]: null,
  [AUTH_REQUEST_KEYS.mfaEnrol]: null,
  [AUTH_REQUEST_KEYS.passwordResetRequest]: null,
  [AUTH_REQUEST_KEYS.passwordResetConfirm]: null,
  [AUTH_REQUEST_KEYS.emailVerify]: null,
  [AUTH_REQUEST_KEYS.invitationStatus]: null,
  [AUTH_REQUEST_KEYS.invitationName]: null,
  [AUTH_REQUEST_KEYS.invitationPassword]: null,
  [AUTH_REQUEST_KEYS.invitationMfaEnrol]: null,
  [AUTH_REQUEST_KEYS.invitationMfaVerify]: null,
  [AUTH_REQUEST_KEYS.invitationAccept]: null,
  [AUTH_REQUEST_KEYS.organisationSelect]: null,
};

const initialSessionState = {
  checked: false,
  authenticated: false,
  reason: null,
  sessionId: null,
  organizationId: null,
  role: null,
  permissions: [],
  user: null,
};

function buildUrl(path: string) {
  return `${AUTH_V2_BASE_PATH}/${path}`;
}

function toAuthUser(rawUser: unknown): AuthUser | null {
  if (!isRecord(rawUser)) {
    return null;
  }

  return {
    id: toStringOrNull(rawUser.id),
    email: toStringOrNull(rawUser.email),
    firstName: toStringOrNull(rawUser.firstName),
    lastName: toStringOrNull(rawUser.lastName),
  };
}

function toAuthOrganisations(value: unknown): AuthOrganisation[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is AuthOrganisation => isRecord(item) && typeof item.id === "string" && typeof item.name === "string",
  );
}

function toErrorDescriptions(details: Record<string, unknown> | null): string[] | undefined {
  if (!details) return undefined;
  if (!Array.isArray(details.passwordErrors)) return undefined;

  const descriptions = details.passwordErrors.reduce<string[]>((messages, item) => {
    if (!isRecord(item)) return messages;

    const message = toStringOrUndefined(item.message);
    if (message) messages.push(message);

    return messages;
  }, []);

  return descriptions.length > 0 ? descriptions : undefined;
}

function toAuthV2Error(payload: unknown, fallbackMessage = "Something went wrong. Please try again."): AuthV2Error {
  const payloadRecord = isRecord(payload) ? payload : null;
  const error = payloadRecord && isRecord(payloadRecord.error) ? payloadRecord.error : null;
  if (error) {
    const details = isRecord(error.details) ? error.details : null;

    return {
      code: toStringOrUndefined(error.code) ?? "UNKNOWN_ERROR",
      message: toStringOrUndefined(error.message) ?? fallbackMessage,
      retryAfterSeconds: typeof details?.retryAfterSeconds === "number" ? details.retryAfterSeconds : undefined,
      mfa: toPendingMfa(details?.mfa),
      workOsCode: toStringOrUndefined(details?.workOsCode),
      userId: toStringOrUndefined(details?.userId),
      pendingAuthenticationToken: toStringOrUndefined(details?.pendingAuthenticationToken),
      organisations: toAuthOrganisations(details?.organisations),
      errorDescriptions: toErrorDescriptions(details),
    };
  }

  return {
    code: "UNKNOWN_ERROR",
    message: toStringOrUndefined(payloadRecord?.status_message) ?? fallbackMessage,
    retryAfterSeconds: undefined,
    mfa: undefined,
    workOsCode: undefined,
    userId: undefined,
    pendingAuthenticationToken: undefined,
    organisations: undefined,
  };
}

function toMfaEnrolmentStart(payload: unknown) {
  if (!isRecord(payload)) {
    return null;
  }

  const error = isRecord(payload.error) ? payload.error : null;
  const details = error && isRecord(error.details) ? error.details : null;
  const enrolmentDetails = details ?? payload;
  const enrollmentToken = toStringOrUndefined(enrolmentDetails.enrollmentToken);
  if (!enrollmentToken) {
    return null;
  }

  return {
    enrollmentToken,
    expiresAt: toStringOrUndefined(enrolmentDetails.expiresAt),
  };
}

function createAuthV2Thunk(type: string, path: string, method = "POST") {
  return createAsyncThunk<
    AuthApiResponse,
    AuthRequestPayload | void,
    { rejectValue: unknown; fulfilledMeta: AuthThunkFulfilledMeta }
  >(type, async (payload, { fulfillWithValue, rejectWithValue }) => {
    const response = await reusableWithoutTokenFetch(buildUrl(path), method, payload, rejectWithValue);
    // When reusableWithoutTokenFetch calls rejectWithValue internally, it returns
    // a RejectWithValue instance. Returning it propagates the rejection to RTK.
    if (!isFetchResult(response)) return response as ReturnType<typeof rejectWithValue>;
    return fulfillWithValue(isRecord(response?.data) ? response.data : {}, { fulfilledAt: Date.now() });
  });
}

export const checkAuthV2Session = createAuthV2Thunk("authV2/checkSession", "session", "GET");
export const authV2PasswordLogin = createAuthV2Thunk("authV2/passwordLogin", "password/login");
export const authV2MagicSend = createAuthV2Thunk("authV2/magicSend", "magic/send");
export const authV2MagicVerify = createAuthV2Thunk("authV2/magicVerify", "magic/verify");
export const authV2MfaVerify = createAuthV2Thunk("authV2/mfaVerify", "mfa/verify");
export const authV2PasswordResetRequest = createAuthV2Thunk("authV2/passwordResetRequest", "password/reset/request");
export const authV2PasswordResetConfirm = createAuthV2Thunk("authV2/passwordResetConfirm", "password/reset/confirm");
export const authV2EmailVerify = createAuthV2Thunk("authV2/emailVerify", "email/verify");
export const authV2MfaEnrol = createAuthV2Thunk("authV2/mfaEnrol", "mfa/enrol");
export const authV2InvitationStatus = createAsyncThunk<AuthApiResponse, { token: string }, { rejectValue: unknown }>(
  "authV2/invitationStatus",
  async ({ token }, { rejectWithValue }) => {
    const response = await reusableWithoutTokenFetch(
      buildUrl(`invitation/status?token=${encodeURIComponent(token)}`),
      "GET",
      undefined,
      rejectWithValue,
    );
    if (!isFetchResult(response)) return response as AuthApiResponse;
    return isRecord(response?.data) ? response.data : {};
  },
);
export const authV2InvitationName = createAuthV2Thunk("authV2/invitationName", "invitation/name");
export const authV2InvitationPassword = createAuthV2Thunk("authV2/invitationPassword", "invitation/password");
export const authV2InvitationMfaEnrol = createAuthV2Thunk("authV2/invitationMfaEnrol", "invitation/mfa/enrol");
export const authV2InvitationMfaVerify = createAuthV2Thunk("authV2/invitationMfaVerify", "invitation/mfa/enrol/verify");
export const authV2InvitationAccept = createAuthV2Thunk("authV2/invitationAccept", "invitation/accept");
export const authV2OrganisationSelect = createAuthV2Thunk("authV2/organisationSelect", "organisation/selection");

const initialState: AuthV2State = {
  session: initialSessionState,
  magicChallenge: null,
  magicOTP: null,
  pendingMfa: null,
  invitationStatus: null,
  loading: initialLoadingState,
  errors: initialErrorState,
};

function addRequestCases(
  builder: ActionReducerMapBuilder<AuthV2State>,
  key: AuthRequestKey,
  thunk: AsyncThunk<
    AuthApiResponse,
    AuthRequestPayload | void,
    { rejectValue: unknown; fulfilledMeta: AuthThunkFulfilledMeta }
  >,
  options?: { includeFulfilled?: boolean; includeRejected?: boolean },
) {
  builder.addCase(thunk.pending, (state) => {
    state.loading[key] = true;
    state.errors[key] = null;
  });

  if (options?.includeFulfilled !== false) {
    builder.addCase(thunk.fulfilled, (state) => {
      state.loading[key] = false;
      state.errors[key] = null;
    });
  }

  if (options?.includeRejected !== false) {
    builder.addCase(thunk.rejected, (state, action) => {
      state.loading[key] = false;
      const parsedError = toAuthV2Error(action.payload);
      state.errors[key] = parsedError;

      if (parsedError.code === "MFA_REQUIRED" && parsedError.mfa) {
        state.pendingMfa = parsedError.mfa;
      }
    });
  }
}

const authV2Slice = createSlice({
  name: "authV2",
  initialState,
  reducers: {
    clearAuthV2Error: (state, action: PayloadAction<AuthRequestKey | undefined>) => {
      if (!action.payload) {
        state.errors = { ...initialErrorState };
        return;
      }

      if (Object.prototype.hasOwnProperty.call(state.errors, action.payload)) {
        state.errors[action.payload] = null;
      }
    },
    setPendingMfa: (
      state,
      action: PayloadAction<{ pendingAuthenticationToken: string; authenticationChallengeId: string } | null>,
    ) => {
      state.pendingMfa = action.payload ?? null;
    },
    clearPendingMfa: (state) => {
      state.pendingMfa = null;
    },
    clearMagicChallenge: (state) => {
      state.magicChallenge = null;
    },
    resetAuthV2State: () => ({
      session: initialSessionState,
      magicChallenge: null,
      magicOTP: null,
      pendingMfa: null,
      invitationStatus: null,
      loading: initialLoadingState,
      errors: initialErrorState,
    }),
  },
  extraReducers: (builder) => {
    addRequestCases(builder, AUTH_REQUEST_KEYS.checkSession, checkAuthV2Session, {
      includeFulfilled: false,
      includeRejected: false,
    });
    addRequestCases(builder, AUTH_REQUEST_KEYS.passwordLogin, authV2PasswordLogin, { includeFulfilled: false });
    addRequestCases(builder, AUTH_REQUEST_KEYS.magicSend, authV2MagicSend, { includeFulfilled: false });
    addRequestCases(builder, AUTH_REQUEST_KEYS.magicVerify, authV2MagicVerify, { includeFulfilled: false });
    addRequestCases(builder, AUTH_REQUEST_KEYS.mfaVerify, authV2MfaVerify, { includeFulfilled: false });
    addRequestCases(builder, AUTH_REQUEST_KEYS.passwordResetRequest, authV2PasswordResetRequest);
    addRequestCases(builder, AUTH_REQUEST_KEYS.passwordResetConfirm, authV2PasswordResetConfirm);
    addRequestCases(builder, AUTH_REQUEST_KEYS.emailVerify, authV2EmailVerify);
    addRequestCases(builder, AUTH_REQUEST_KEYS.mfaEnrol, authV2MfaEnrol, { includeFulfilled: false });
    builder.addCase(authV2InvitationName.pending, (state) => {
      state.loading[AUTH_REQUEST_KEYS.invitationName] = true;
      state.errors[AUTH_REQUEST_KEYS.invitationName] = null;
    });
    builder.addCase(authV2InvitationName.fulfilled, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.invitationName] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationName] = null;
      if (state.invitationStatus?.setup) {
        state.invitationStatus.setup = {
          ...state.invitationStatus.setup,
          ...action.payload,
        };
      }
    });
    builder.addCase(authV2InvitationName.rejected, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.invitationName] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationName] = toAuthV2Error(action.payload);
    });
    builder.addCase(authV2InvitationPassword.pending, (state) => {
      state.loading[AUTH_REQUEST_KEYS.invitationPassword] = true;
      state.errors[AUTH_REQUEST_KEYS.invitationPassword] = null;
    });
    builder.addCase(authV2InvitationPassword.fulfilled, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.invitationPassword] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationPassword] = null;
      if (state.invitationStatus?.setup) {
        state.invitationStatus.setup = {
          ...state.invitationStatus.setup,
          ...action.payload,
        };
      }
    });
    builder.addCase(authV2InvitationPassword.rejected, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.invitationPassword] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationPassword] = toAuthV2Error(action.payload);
    });
    builder.addCase(authV2InvitationMfaEnrol.pending, (state) => {
      state.loading[AUTH_REQUEST_KEYS.invitationMfaEnrol] = true;
      state.errors[AUTH_REQUEST_KEYS.invitationMfaEnrol] = null;
    });
    builder.addCase(authV2InvitationMfaEnrol.fulfilled, (state) => {
      state.loading[AUTH_REQUEST_KEYS.invitationMfaEnrol] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationMfaEnrol] = null;
    });
    builder.addCase(authV2InvitationMfaEnrol.rejected, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.invitationMfaEnrol] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationMfaEnrol] = toAuthV2Error(action.payload);
    });
    builder.addCase(authV2InvitationMfaVerify.pending, (state) => {
      state.loading[AUTH_REQUEST_KEYS.invitationMfaVerify] = true;
      state.errors[AUTH_REQUEST_KEYS.invitationMfaVerify] = null;
    });
    builder.addCase(authV2InvitationMfaVerify.fulfilled, (state) => {
      state.loading[AUTH_REQUEST_KEYS.invitationMfaVerify] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationMfaVerify] = null;
    });
    builder.addCase(authV2InvitationMfaVerify.rejected, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.invitationMfaVerify] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationMfaVerify] = toAuthV2Error(action.payload);
    });
    builder.addCase(authV2InvitationAccept.pending, (state) => {
      state.loading[AUTH_REQUEST_KEYS.invitationAccept] = true;
      state.errors[AUTH_REQUEST_KEYS.invitationAccept] = null;
    });
    builder.addCase(authV2InvitationAccept.fulfilled, (state) => {
      state.loading[AUTH_REQUEST_KEYS.invitationAccept] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationAccept] = null;
    });
    builder.addCase(authV2InvitationAccept.rejected, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.invitationAccept] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationAccept] = toAuthV2Error(action.payload);
    });
    builder.addCase(authV2InvitationStatus.pending, (state) => {
      state.loading[AUTH_REQUEST_KEYS.invitationStatus] = true;
      state.errors[AUTH_REQUEST_KEYS.invitationStatus] = null;
    });
    builder.addCase(authV2InvitationStatus.fulfilled, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.invitationStatus] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationStatus] = null;

      const payload = action.payload ?? {};
      const rawInvitation = isRecord(payload.invitation) ? payload.invitation : null;
      const rawSetup = isRecord(payload.setup) ? payload.setup : null;
      const rawAccount = isRecord(payload.account) ? payload.account : null;

      state.invitationStatus = {
        invitation: rawInvitation
          ? {
              email: toStringOrNull(rawInvitation.email),
              tenancyId: toStringOrNull(rawInvitation.tenancyId),
              tenancyName: toStringOrNull(rawInvitation.tenancyName),
              organisationName: toStringOrNull(rawInvitation.organisationName),
              state: toStringOrNull(rawInvitation.state),
              expiresAt: toStringOrNull(rawInvitation.expiresAt),
            }
          : null,
        setup: rawSetup
          ? {
              nameConfigured: Boolean(rawSetup.nameConfigured),
              passwordConfigured: Boolean(rawSetup.passwordConfigured),
              mfaConfigured: Boolean(rawSetup.mfaConfigured),
            }
          : null,
        account: rawAccount
          ? {
              existing: Boolean(rawAccount.existing),
              requiresSignInToAccept: Boolean(rawAccount.requiresSignInToAccept),
            }
          : null,
      };
    });
    builder.addCase(authV2InvitationStatus.rejected, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.invitationStatus] = false;
      state.errors[AUTH_REQUEST_KEYS.invitationStatus] = toAuthV2Error(action.payload);
    });

    builder.addCase(checkAuthV2Session.fulfilled, (state, action) => {
      const payload = action.payload ?? {};
      const authenticated = Boolean(payload.authenticated);

      state.loading[AUTH_REQUEST_KEYS.checkSession] = false;
      state.errors[AUTH_REQUEST_KEYS.checkSession] = null;
      state.session = {
        checked: true,
        authenticated: Boolean(payload.authenticated),
        reason: authenticated ? null : (toStringOrNull(payload.reason) ?? "unauthenticated"),
        sessionId: toStringOrNull(payload.sessionId),
        organizationId: toStringOrNull(payload.organizationId),
        role: toStringOrNull(payload.role),
        permissions: Array.isArray(payload.permissions) ? payload.permissions : [],
        user: toAuthUser(payload.user),
      };
    });

    builder.addCase(checkAuthV2Session.rejected, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.checkSession] = false;
      state.errors[AUTH_REQUEST_KEYS.checkSession] = toAuthV2Error(action.payload, "Unable to check session.");
      state.session = {
        checked: true,
        authenticated: false,
        reason: "session_check_failed",
        sessionId: null,
        organizationId: null,
        role: null,
        permissions: [],
        user: null,
      };
    });

    builder.addCase(authV2MagicSend.fulfilled, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.magicSend] = false;
      state.errors[AUTH_REQUEST_KEYS.magicSend] = null;
      state.magicChallenge = {
        challengeId: toStringOrNull(action.payload.challengeId),
        expiresAt: toStringOrNull(action.payload.expiresAt),
        email: toStringOrNull(action.meta.arg?.email),
      };
      state.magicOTP = {
        email: toStringOrNull(action.meta.arg?.email),
        triggeredAt: action.meta.fulfilledAt,
      };
    });

    builder.addCase(authV2PasswordLogin.fulfilled, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.passwordLogin] = false;
      state.errors[AUTH_REQUEST_KEYS.passwordLogin] = null;
      if (!action.payload?.authenticated) {
        return;
      }

      state.pendingMfa = null;
      state.session = {
        ...state.session,
        checked: true,
        authenticated: true,
        reason: null,
        organizationId: toStringOrNull(action.payload.organizationId),
        user: toAuthUser(action.payload.user),
      };
    });

    builder.addCase(authV2MagicVerify.fulfilled, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.magicVerify] = false;
      state.errors[AUTH_REQUEST_KEYS.magicVerify] = null;
      if (!action.payload?.authenticated) {
        return;
      }

      state.pendingMfa = null;
      state.session = {
        ...state.session,
        checked: true,
        authenticated: true,
        reason: null,
        organizationId: toStringOrNull(action.payload.organizationId),
        user: toAuthUser(action.payload.user),
      };
    });

    builder.addCase(authV2MfaVerify.fulfilled, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.mfaVerify] = false;
      state.errors[AUTH_REQUEST_KEYS.mfaVerify] = null;
      if (!action.payload?.authenticated) {
        return;
      }

      state.pendingMfa = null;
      state.session = {
        ...state.session,
        checked: true,
        authenticated: true,
        reason: null,
        organizationId: toStringOrNull(action.payload.organizationId),
        user: toAuthUser(action.payload.user),
      };
    });

    // The enrolment response contains a TOTP secret — intentionally kept out of
    // persisted state. The calling component reads it from the thunk result directly.
    builder.addCase(authV2MfaEnrol.fulfilled, (state) => {
      state.loading[AUTH_REQUEST_KEYS.mfaEnrol] = false;
      state.errors[AUTH_REQUEST_KEYS.mfaEnrol] = null;
    });

    addRequestCases(builder, AUTH_REQUEST_KEYS.organisationSelect, authV2OrganisationSelect, {
      includeFulfilled: false,
    });
    builder.addCase(authV2OrganisationSelect.fulfilled, (state, action) => {
      state.loading[AUTH_REQUEST_KEYS.organisationSelect] = false;
      state.errors[AUTH_REQUEST_KEYS.organisationSelect] = null;
      if (!action.payload?.authenticated) {
        return;
      }

      state.session = {
        ...state.session,
        checked: true,
        authenticated: true,
        reason: null,
        organizationId: toStringOrNull(action.payload.organizationId),
        user: toAuthUser(action.payload.user),
      };
    });
  },
});

const authV2Selector = (state: RootState) => state.authV2;

export const { clearAuthV2Error, clearMagicChallenge, clearPendingMfa, resetAuthV2State, setPendingMfa } =
  authV2Slice.actions;
export { authV2Selector, AUTH_REQUEST_KEYS, toAuthV2Error, toMfaEnrolmentStart };
export default authV2Slice;
